import { describeMutationError } from "@api/mutationErrors";
import {
  Button,
  Dialog,
  FieldError,
  Input,
} from "@scribenote/brian-design-system";
import { useState } from "react";

import {
  BOARD_VIEW_LABEL,
  BOARD_VIEWS,
  type BoardRotation,
  type ListedDisplay,
} from "./displaysClient";
import { useUpdateDisplay } from "./displaysQueries";

/** Mirrors brian `BoardRotation`'s bounds. */
const MAX_SECONDS = 600;

/** How long a clinic board shows each view; 0 leaves a view out. */
export const BoardViewsButton = ({
  display,
  saved,
  sessionKey,
}: {
  display: ListedDisplay;
  saved: BoardRotation;
  sessionKey: string;
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<BoardRotation | null>(null);
  const update = useUpdateDisplay(sessionKey);
  const rotation = draft ?? saved;
  const nothing = BOARD_VIEWS.every(({ seconds }) => rotation[seconds] === 0);
  const close = () => {
    setOpen(false);
    setDraft(null);
    update.reset();
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Views
      </Button>
      <Dialog
        open={open}
        onClose={close}
        size="sm"
        title={`Views on ${display.name}`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="accent"
              busy={update.isPending}
              disabled={!draft || nothing}
              onClick={() =>
                update.mutate(
                  { displayId: display.id, change: { rotation } },
                  { onSuccess: close },
                )
              }
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-body">
            Seconds the screen shows each view before the next. 0 leaves a view
            out.
          </p>
          {BOARD_VIEWS.map(({ view, seconds: key }) => (
            <Input
              key={key}
              label={BOARD_VIEW_LABEL[view]}
              type="number"
              min={0}
              max={MAX_SECONDS}
              value={rotation[key]}
              onChange={(event) => {
                const seconds = Number.parseInt(event.target.value, 10);
                setDraft({
                  ...rotation,
                  [key]: Number.isFinite(seconds)
                    ? Math.min(Math.max(seconds, 0), MAX_SECONDS)
                    : 0,
                });
              }}
            />
          ))}
          {nothing && <FieldError>Show at least one view.</FieldError>}
          {update.isError && (
            <FieldError>
              {describeMutationError(update.error, "save the views")}
            </FieldError>
          )}
        </div>
      </Dialog>
    </>
  );
};
